import { createContext, useContext, useRef, useSyncExternalStore } from 'react';
import type { CardDefId, CellId, GameState, HackCandidate, InstanceId, PlayerId } from '@cards/engine';
import type { LogEntry } from '../types.js';

export type TurnBanner = 'reveal' | 'effects' | 'combat' | 'deaths' | 'round_end';

/**
 * One source of truth for the deliberately readable turn sequence. Keeping
 * the waits and CSS animation durations together prevents HP changes from
 * racing ahead of the pulse that explains them.
 */
export const TURN_PLAYBACK_TIMING = Object.freeze({
  // Banner/notice DOM is globally suppressed by the kit stylesheet. One event
  // loop tick preserves an observable boundary without a perceptible pause.
  hiddenHudStep: 1,
  // Beat after every placed card lines up face-down (both players, all at once)
  // before the one-by-one flip reveals begin.
  revealLineup: 520,
  // Reveal total: the card holds face-down (a beat so the back reads), then
  // flips to its face (content swap at the edge-on point ~60%) and pops with an
  // accent. The hold is baked into the cards-reveal-card keyframe.
  cardReveal: 620,
  // The gulp that eats the host, AFTER the card lying on it has flipped face-up
  // (the ghost's animation is delayed by cardReveal for exactly that). Its own
  // window because it is the point of the beat: the swallow used to be squeezed
  // into the flip's 372ms tail, six poses deep, which read as a vibration
  // rather than as something being eaten — and, hidden under the ghost, the
  // flip itself was never seen at all.
  hostSwallow: 860,
  // One animation on one clock for an overlay receiver: the reveal flip while
  // the card still lies on its host (cardReveal), then the gulp that climbs it
  // into the slot (hostSwallow). Must stay that sum — the percentages in
  // cards-reveal-card-devouring are the split, and the JS only aims its
  // face-swap timer at the flip's edge-on frame.
  overlayReveal: 620 + 860,
  // One drawn card, one beat. The deck→hand trip itself is a spring-timed
  // overlay flight that outlives this wait and lands on its own (Hand.tsx), so
  // this paces the deal, not the travel: long enough that a multi-card draw
  // reads as separate cards leaving the pile. It is also what makes that draw
  // legible at all — the cue is a scalar, so without a render between two
  // CardDrawn events only the last card would ever be animated.
  cardDraw: 380,
  attackTravel: 520,
  attackImpact: 650,
  // The first frames of attackImpact, not a beat of their own: both cards hold
  // dead still at the contact point, lit, before anything recoils. That freeze
  // is what makes a hit land; without it the strike and the bounce read as one
  // smooth slide. CSS-only — playback still waits attackImpact in total.
  hitStop: 70,
  statusChange: 420,
  // A stat change plays as one merged ±ATK/±HP beat (see the StatChanged case
  // in turnPlayback), and a number landing on its own badge needs less air than
  // a hit or a death. Drains share the window: same shape, opposite colour.
  statBuff: 260,
  death: 650,
  captureTransfer: 520,
  tokenCreate: 420,
  // Decay-created fragments all launch together the moment their source's
  // death starts, growing and solidifying while they fly to their cells; the
  // whole burst shares this one window alongside the death animation.
  decayFragment: 900,
  consume: 460,
  // The beat that names the meal while the host is still the real occupant,
  // with the face-down card that will eat it lying on top. A host grabbed on the
  // frame its own name appears reads as a rendering fault. Short: it is a
  // hand-off, not an event of its own.
  overlayHostExpose: 240,
  transform: 520,
  explanation: 700,
  // A fizzled FIELD card flips face-up on the cell it could not have — lying on
  // the occupant that denied it — and then has to leave. It used to be simply
  // unmounted: a card that had just been dealt with and revealed blinked out of
  // existence between two frames, which reads as a rendering fault rather than
  // as a play failing. Short, and after the notice has already explained it: the
  // fade is the departure, not the explanation.
  fieldFizzleFade: 260,
  timedDestroy: 560,
  hackCandidates: 700,
  hackSelected: 650,
  effectTravel: 460,
  // A devour is three beats, not a slide: the vines reach out and open around
  // the prey, the trap snaps shut on it, and only then is the catch dragged in
  // and swallowed. One travel window cannot hold all three legibly.
  devour: 760,
  cellPulse: 460,
  castImpact: 520,
  unitMove: 380,
});

export type PlaybackSpeed = 1 | 2;
export type PlaybackTiming = { readonly [Beat in keyof typeof TURN_PLAYBACK_TIMING]: number };

/** At ×2 the frozen frame of a hit still has to register (03 §5). */
const HIT_STOP_FLOOR_MS = 40;
/** At ×2 a number landing on a badge still needs this long to be read (03 §5). */
const NUMBER_FLOOR_MS = 250;
/** Beats that show a number: a stat roll, a status ±N, a damage or a cell ±N. */
const NUMBER_BEATS = ['statBuff', 'statusChange', 'cellPulse', 'attackImpact', 'castImpact'] as const;
/** Beats that hold a hit-stop at a share of their window fixed by the keyframes. */
const HIT_STOP_BEATS = ['attackImpact', 'castImpact'] as const;
/** Beats that are only travel: reduced motion has nothing to show in them (03 §6). */
const TRAVEL_BEATS = ['attackTravel', 'effectTravel', 'unitMove', 'captureTransfer', 'revealLineup'] as const;
const REDUCED_TRAVEL_MS = 120;

const timingCache = new Map<string, PlaybackTiming>();

/**
 * The beat table for a playback speed (03 §5, §6). ×2 halves every beat, but a
 * hit-stop never drops under 40ms — so a beat that holds one keeps the window
 * its keyframe share needs — and a beat that shows a number stays at least
 * 250ms. Reduced motion plays at ×2 and cuts the travel-only beats to 120ms:
 * with nothing moving, a full travel window is a frozen frame to wait out.
 * The CSS reads the same table through `--beat-*`, so animations and waits
 * stay one clock.
 */
export function playbackTiming(speed: PlaybackSpeed, reducedMotion = false): PlaybackTiming {
  if (speed === 1 && !reducedMotion) return TURN_PLAYBACK_TIMING;
  const key = `${speed}:${reducedMotion ? 'reduced' : 'full'}`;
  const cached = timingCache.get(key);
  if (cached !== undefined) return cached;
  const timing: Record<keyof typeof TURN_PLAYBACK_TIMING, number> = { ...TURN_PLAYBACK_TIMING };
  for (const beat of Object.keys(timing) as (keyof typeof TURN_PLAYBACK_TIMING)[]) {
    if (beat !== 'hiddenHudStep') timing[beat] = Math.round(TURN_PLAYBACK_TIMING[beat] / 2);
  }
  timing.hitStop = Math.max(HIT_STOP_FLOOR_MS, timing.hitStop);
  for (const beat of HIT_STOP_BEATS) {
    timing[beat] = Math.max(timing[beat], Math.ceil(
      HIT_STOP_FLOOR_MS * TURN_PLAYBACK_TIMING[beat] / TURN_PLAYBACK_TIMING.hitStop));
  }
  for (const beat of NUMBER_BEATS) timing[beat] = Math.max(timing[beat], NUMBER_FLOOR_MS);
  if (reducedMotion) for (const beat of TRAVEL_BEATS) timing[beat] = REDUCED_TRAVEL_MS;
  timing.overlayReveal = timing.cardReveal + timing.hostSwallow;
  const frozen = Object.freeze(timing);
  timingCache.set(key, frozen);
  return frozen;
}

export interface PlaybackNotice {
  kind: 'creation' | 'assembly' | 'resource' | 'timed' | 'signal' | 'hack' | 'reaction' | 'virus' | 'capture' | 'attack' | 'spell' | 'phase';
  text: string;
}

export interface HackCue {
  hackId: string;
  attackerId: InstanceId;
  initiatorPlayerId: PlayerId;
  candidates: readonly HackCandidate[];
  selected: HackCandidate | undefined;
  phase: 'candidates' | 'selected';
}

/**
 * Seeded selection among public board cards (RandomUnitSelectionResolved):
 * first every candidate glows, then only the selected card stays highlighted.
 * Modeled on HackCue's two-phase presentation.
 */
export interface RandomPickCue {
  sourceId?: InstanceId;
  candidates: readonly { instanceId: InstanceId; cellId: CellId }[];
  selected: { instanceId: InstanceId; cellId: CellId } | undefined;
  phase: 'candidates' | 'selected';
}

export interface CombatStrike {
  attackGroupId?: string;
  sourceInstanceId: InstanceId;
  sourceCellId: CellId;
  targetCellId?: CellId;
  targetInstanceId?: InstanceId;
  targetPlayerId?: PlayerId;
  amount: number;
  partIndex?: number;
  partCount?: number;
  phase: 'travel' | 'impact';
}

/** One combat step; a facing or displaced simultaneous strike may share its beat. */
export interface CombatCue extends CombatStrike {
  reciprocal?: CombatStrike;
  /** A second, independent strike that resolves in the same combat wave. */
  parallel?: CombatStrike;
}

export interface EffectCue {
  /** `heat`/`freeze`: the card's Перегрев/Заморозка moved (03 §4.6–4.7). */
  kind: 'capture' | 'virus' | 'move' | 'stat' | 'effect' | 'shield' | 'hack' | 'damage' | 'avoid' | 'counter' | 'sun' | 'heal' | 'heat' | 'freeze';
  /** `skip`: a frozen card's attack was spent standing still (FreezeChanged, combat_skip). */
  phase: 'departure' | 'arrival' | 'attach' | 'trigger' | 'remove' | 'positive' | 'negative' | 'impact' | 'deplete' | 'skip';
  targetInstanceId?: InstanceId;
  targetPlayerId?: PlayerId;
  /** Floating ±N shown over the card (counter changes and heals). */
  delta?: number;
  /**
   * A whole run of StatChanged merged into one beat, shown as ±N over the stat's
   * own badge. `health` already folds maxHealth into the current-HP move, so a
   * `+1/+1` growth reads as one HP gain instead of two anonymous `+1`.
   */
  statDeltas?: { attack?: number; health?: number };
}

/** A causal board-space link for non-combat card effects such as absorption. */
export interface EffectPathCue {
  kind: 'absorb' | 'devour' | 'trigger' | 'death-fall' | 'status' | 'heal';
  sourceCellId: CellId;
  /** Public glyph that falls onto the target for a reactive death strike. */
  sourceDefId?: CardDefId;
  targetCellId?: CellId;
  targetPlayerId?: PlayerId;
  targetInstanceId?: InstanceId;
  amount?: number;
  phase: 'travel' | 'impact';
}

/** A revealed spell/reaction has no board cell, so its public glyph becomes the origin. */
export interface CastCue {
  sourceDefId: CardDefId;
  /** Queue orb the cast fiber grows out of, when the caster is still on the rail. */
  sourceInstanceId?: InstanceId;
  targetCellId?: CellId;
  targetPlayerId?: PlayerId;
  amount?: number;
  phase: 'travel' | 'impact';
}

/** One-shot card occupying the shared reveal rail above/below the locations. */
export interface OneShotLineupCard {
  instanceId: InstanceId;
  playerId: PlayerId;
  defId?: CardDefId;
  kind: 'SPELL' | 'VIRUS' | 'REACTION';
  faceDown: boolean;
  revealing: boolean;
}

/** A card that has just been drawn into its owner's hand. */
export interface DrawCue {
  playerId: PlayerId;
  instanceId: InstanceId;
  /** Monotonic playback-local token; lets a hand consume each draw exactly once. */
  sequence: number;
}

/** Location-scoped effects and token creation need a cue even when the cell is empty. */
export interface CellCue {
  cellId: CellId;
  kind: 'effect' | 'creation' | 'assembly' | 'trigger' | 'avoid';
  phase: 'positive' | 'negative' | 'remove';
  delta?: number;
}

/**
 * A unit that just moved cells. The board reads this to FLIP-slide the (already
 * relocated) card from its previous cell to the new one, so the move reads as a
 * glide across the field instead of a fade-out here / fade-in there teleport.
 */
export interface MoveCue {
  instanceId: InstanceId;
  fromCellId: CellId;
}

/** A token materialises from the most recently resolved death in its chain. */
export interface SpawnCue {
  sourceCellId: CellId;
  /** Decay bursts fly several fragments out of one death simultaneously. */
  targetInstanceIds: readonly InstanceId[];
  decayFragment?: boolean;
}

/**
 * An overlay host being swallowed: the receiver flips up in the host's cell
 * while a ghost of the host renders above it and is pulled into the card.
 */
export interface HostAbsorbCue {
  cellId: CellId;
  defId: CardDefId;
  /**
   * `expose` hands the cell over: the host is still the real occupant and the
   * placing player's staged card lifts off it. `swallow` is the meal itself —
   * the host is gone from state and the ghost stands in for it.
   */
  phase: 'expose' | 'swallow';
  /**
   * The host's face as it stood the moment it was eaten. The ghost has to be a
   * continuation of the card that was standing here, so it carries the stat
   * strip too — without it the host visibly drops its numbers on the frame the
   * ghost takes over.
   */
  stats?: { attack: number; health: number };
  /**
   * Stats the receiver took off the host, shown rising out of the gulp. Absent
   * when the swallow moved nothing — then there is no number worth a beat.
   */
  gained?: { attack: number; health: number };
}

/** Two consumed cards converge on the cell where their replacement is formed. */
export interface AssemblyCue {
  fragmentIds: readonly [InstanceId, InstanceId];
  robotCellId: CellId;
}

/**
 * A deck passive just fired. The owning seat's HUD badge pulses in its own
 * accent colour so the round-end heal / escalating hit / structure buff / echo
 * reads as coming from the passive, not from a nameless generic effect.
 */
export type PassivePulseVariant =
  | 'heal'
  | 'charge'
  | 'discharge'
  | 'reset'
  | 'buff'
  | 'reserve'
  | 'echo';

export interface PassivePulseCue {
  playerId: PlayerId;
  variant: PassivePulseVariant;
  /** Floating ±N shown over the badge (heal, escalating charge, reset drain). */
  delta?: number;
}

export interface TurnPlaybackVisuals {
  isPlaying: boolean;
  banner: TurnBanner | undefined;
  enteringId: InstanceId | undefined;
  /** Cards shown face-down (card back) — the first half of a reveal flip. */
  faceDownIds: readonly InstanceId[];
  /** Face-down one-shot lineup; each entry flips in its persistent field rail. */
  oneShotLineup: readonly OneShotLineupCard[];
  /** Explicit hand-arrival cue; unlike a state refresh, this denotes a real draw. */
  drawCue: DrawCue | undefined;
  /** Cells showing a face-down back placeholder before their card flips up: at
   * round start every placed card (both players) lines up back-up here, then
   * each cell is cleared as its card reveals. */
  faceDownCells: readonly CellId[];
  /** FIELD occupants are withheld until their own UnitEnteredPlay reveal. */
  withheldFieldEntryIds: readonly InstanceId[];
  /** A FIELD card replaces an already visible lineup back, not a fresh arrival. */
  lineupHandoffIds: readonly InstanceId[];
  /** FIELD intent currently flipping up to explain why it fizzled. */
  fieldFizzleCue: {
    readonly instanceId: InstanceId;
    readonly cellId: CellId;
    /** Set for the last beat, while the card fades off the cell it was denied. */
    readonly leaving?: true;
  } | undefined;
  /** Lethally damaged cards kept on their cells while their trigger chain plays. */
  fracturedIds: readonly InstanceId[];
  /** Logically present corpses hidden from their board cells after lethal impact. */
  departedIds: readonly InstanceId[];
  /** The fractured card currently dissolving and releasing its cell. */
  dyingId: InstanceId | undefined;
  consumingIds: readonly InstanceId[];
  transformingId: InstanceId | undefined;
  timedDestroyingId: InstanceId | undefined;
  notice: PlaybackNotice | undefined;
  combatCue: CombatCue | undefined;
  effectCue: EffectCue | undefined;
  effectPathCue: EffectPathCue | undefined;
  castCue: CastCue | undefined;
  cellCue: CellCue | undefined;
  moveCue: MoveCue | undefined;
  spawnCue: SpawnCue | undefined;
  hostAbsorbCue: HostAbsorbCue | undefined;
  assemblyCue: AssemblyCue | undefined;
  hackCue: HackCue | undefined;
  randomPickCue: RandomPickCue | undefined;
  passivePulse: PassivePulseCue | undefined;
  /**
   * What a skipped batch changed (03 §5): the cards and seats whose numbers or
   * statuses moved while the animation was cut short, ringed for a moment so
   * the result is still readable.
   */
  skipChanges?: SkipChanges | undefined;
  /** The dying card is melting (Перегрев 4), not crumbling (03 §4.6). */
  meltingId?: InstanceId | undefined;
  skip: () => void;
}

export interface SkipChanges {
  instanceIds: readonly InstanceId[];
  playerIds: readonly PlayerId[];
}

/** Nothing is playing: every cue absent. Also the base a test builds a cue on. */
export const IDLE_PLAYBACK: TurnPlaybackVisuals = {
  isPlaying: false,
  banner: undefined,
  enteringId: undefined,
  faceDownIds: [],
  oneShotLineup: [],
  drawCue: undefined,
  faceDownCells: [],
  withheldFieldEntryIds: [],
  lineupHandoffIds: [],
  fieldFizzleCue: undefined,
  fracturedIds: [],
  departedIds: [],
  dyingId: undefined,
  consumingIds: [],
  transformingId: undefined,
  timedDestroyingId: undefined,
  notice: undefined,
  combatCue: undefined,
  effectCue: undefined,
  effectPathCue: undefined,
  castCue: undefined,
  cellCue: undefined,
  moveCue: undefined,
  spawnCue: undefined,
  hostAbsorbCue: undefined,
  assemblyCue: undefined,
  hackCue: undefined,
  randomPickCue: undefined,
  passivePulse: undefined,
  skip: () => undefined,
};

/**
 * Playback state lives in an external store rather than React state: a replay
 * step used to funnel through ~16 useState slots at the App level and
 * re-render the whole game tree twice per event. Components now subscribe to
 * exactly the slice they present (`useTurnPlaybackSelector`), so a cue only
 * re-renders the cards it concerns.
 */
export interface TurnPlaybackStore {
  subscribe(listener: () => void): () => void;
  getVisuals(): TurnPlaybackVisuals;
  /** The deliberately slowed presentation state (never ahead of the server). */
  getState(): GameState;
  getVisibleLog(): readonly LogEntry[];
}

const IDLE_STORE: TurnPlaybackStore = {
  subscribe: () => () => undefined,
  getVisuals: () => IDLE_PLAYBACK,
  getState: () => {
    throw new Error('TurnPlaybackStore.getState requires an active game provider.');
  },
  getVisibleLog: () => [],
};

export const TurnPlaybackContext = createContext<TurnPlaybackStore>(IDLE_STORE);

export function useTurnPlaybackVisuals(): TurnPlaybackVisuals {
  const store = useContext(TurnPlaybackContext);
  return useSyncExternalStore(store.subscribe, store.getVisuals, store.getVisuals);
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]));
}

/**
 * Subscribes to a derived slice of the playback visuals. The component only
 * re-renders when the selected slice changes (`isEqual`, shallow by default) —
 * cue objects keep their identity between store patches, so picking them into
 * a flat object is enough for stability.
 */
export function useTurnPlaybackSelector<T>(
  selector: (visuals: TurnPlaybackVisuals) => T,
  isEqual: (previous: T, next: T) => boolean = shallowEqual,
): T {
  const store = useContext(TurnPlaybackContext);
  const cacheRef = useRef<{ value: T } | undefined>(undefined);
  const getSnapshot = (): T => {
    const next = selector(store.getVisuals());
    const cached = cacheRef.current;
    if (cached !== undefined && isEqual(cached.value, next)) return cached.value;
    cacheRef.current = { value: next };
    return next;
  };
  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
